import cookies from "js-cookie";
import {
  CONSENT_CHANGE_EVENT,
  ONETRUST_COOKIE_ANALYTICS_CONSENT_KEY,
  ONETRUST_COOKIE_FUNCTIONAL_CONSENT_KEY,
  ONETRUST_COOKIE_NAME,
  ONETRUST_COOKIE_SOCIAL_MEDIA_CONSENT_KEY,
  ONETRUST_COOKIE_STRICTLY_NECESSARY_CONSENT_KEY,
  ONETRUST_COOKIE_TARGETING_CONSENT_KEY,
} from "./constants";

interface ConsentState {
  hasStrictlyNecessaryConsent: boolean;
  hasAnalyticsConsent: boolean;
  hasFunctionalCookieConsent: boolean;
  hasTargetingConsent: boolean;
  hasSocialMediaTrackConsent: boolean;
}
type NativeOneTrustListener = (e: Event) => void;

type ConsentEventListener = (consentState: ConsentState) => void;

const listenerRegistry = new Map<Symbol, NativeOneTrustListener>();

/**
 * Parses any cookie value and returns an object with all the values
 * @param {mixed} value Value string from cookie, null if there is no cookie
 * @returns {object}
 */
export const parseCookie = (value) => {
  return Object.fromEntries(new URLSearchParams(value));
};

/**
 * Return the state of the user's consent
 *
 * @returns {ConsentState} current state of the user's consent
 */
export const getConsentState = (): ConsentState => {
  const cookieValue = cookies.get(ONETRUST_COOKIE_NAME);
  const cookieData = parseCookie(cookieValue);

  const strictlyNecessary = `${ONETRUST_COOKIE_STRICTLY_NECESSARY_CONSENT_KEY}:1`;
  const analytics = `${ONETRUST_COOKIE_ANALYTICS_CONSENT_KEY}:1`;
  const functional = `${ONETRUST_COOKIE_FUNCTIONAL_CONSENT_KEY}:1`;
  const targeting = `${ONETRUST_COOKIE_TARGETING_CONSENT_KEY}:1`;
  const socialMedia = `${ONETRUST_COOKIE_SOCIAL_MEDIA_CONSENT_KEY}}:1`;
  const cookieDataGroup = cookieData.groups || "";

  return {
    hasStrictlyNecessaryConsent: cookieDataGroup.includes(strictlyNecessary),
    hasAnalyticsConsent: cookieDataGroup.includes(analytics),
    hasFunctionalCookieConsent: cookieDataGroup.includes(functional),
    hasTargetingConsent: cookieDataGroup.includes(targeting),
    hasSocialMediaTrackConsent: cookieDataGroup.includes(socialMedia),
  };
};

const getConsentStateFromEvent = (e): ConsentState => ({
  hasStrictlyNecessaryConsent: e.includes(
    ONETRUST_COOKIE_STRICTLY_NECESSARY_CONSENT_KEY
  ),
  hasAnalyticsConsent: e.includes(ONETRUST_COOKIE_ANALYTICS_CONSENT_KEY),
  hasFunctionalCookieConsent: e.includes(
    ONETRUST_COOKIE_FUNCTIONAL_CONSENT_KEY
  ),
  hasTargetingConsent: e.includes(ONETRUST_COOKIE_TARGETING_CONSENT_KEY),
  hasSocialMediaTrackConsent: e.includes(
    ONETRUST_COOKIE_SOCIAL_MEDIA_CONSENT_KEY
  ),
});

/**
 * Attaches event listener to window that reacts to changes in consent for analytics cookies
 * Checks for the existence of a listener to prevent duplications
 * @param {InitializeParams} params  contains trackingData: {user details, subscriptions, feature flags}
 * @returns {Symbol} Id for the registered event listener
 */
export const addConsentListener = (callback: ConsentEventListener): Symbol => {
  const listenerFunction = (event) => {
    const detail = event.detail;
    const consentState = getConsentStateFromEvent(detail);
    callback(consentState);
  };
  
  const listenerId = Symbol();
  listenerRegistry.set(listenerId, listenerFunction);
  window.addEventListener(CONSENT_CHANGE_EVENT, listenerFunction);
  return listenerId;
};

/**
 *
 * @param id id from the listener previously registered using the function addConsentListener
 */
export const removeConsentListenerById = (id: Symbol): void => {
  if (listenerRegistry.has(id)) {
    const listenerFunction = listenerRegistry.get(id) as NativeOneTrustListener;
    window.removeEventListener(CONSENT_CHANGE_EVENT, listenerFunction);
  }
};
